'use client';

import cn from 'classnames';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';
import { StyledContainer } from '@/ui-components/atoms/Layout/StyledContainer/StyledContainer';
import { Header } from '../Header/Header';
import { ButtonProps } from '@/ui-components/atoms/Button/Button';
import { BreadcrumbsProps } from '@/ui-components/molecules/Breadcrumbs/Breadcrumbs';
import styles from './herocarousel.module.scss';

export interface HeroCarouselHero {
    backgroundColor?: string;
    backgroundImage?: string;
    backgroundImageMobile?: string;
    backgroundGradient?: boolean;
    headline?: React.ReactNode;
    subheadline?: React.ReactNode;
    channelNumber?: number;
    links?: ButtonProps[];
    foregroundImage?: React.ReactElement<ImageProps>;
    foregroundImageMobile?: React.ReactElement<ImageProps>;
    foregroundPositionMobile?: 'Below Text' | 'Above Text';
    carouselImage?: React.ReactElement<ImageProps>;
    breadcrumbs?: React.ReactElement<BreadcrumbsProps>;
    copyright?: React.ReactNode;
    slideButtons?: React.ReactNode[];
    height?: string;
    name?: string;
    pznMessage?: React.ReactNode;
}

export function Hero({
    backgroundColor,
    backgroundImage,
    backgroundImageMobile,
    backgroundGradient,
    headline,
    subheadline,
    channelNumber,
    links,
    foregroundImage,
    foregroundImageMobile,
    foregroundPositionMobile = 'Above Text',
    slideButtons,
    breadcrumbs,
    copyright,
    height = 'auto',
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    name = '', // this is needed for analytics in HeroCarousel.tsx
    pznMessage,
}: HeroCarouselHero): React.JSX.Element {
    const containerClasses = cn(styles.container, 'heroNavThemeColors', {
        [styles.bgGradient]: backgroundGradient,
    });

    const gridClasses = cn(styles.gridContainer, {
        [styles.fgBelow]: foregroundPositionMobile === 'Below Text',
    });

    const headerColClasses = cn(styles.headerCol, {
        [styles.thumbnailsAbove]: foregroundPositionMobile === 'Below Text',
    });

    const foregroundDesktopImageClasses = cn({
        [styles.foregroundDesktop]: foregroundImageMobile !== undefined,
    });

    const foregroundMobileImageClasses = cn({
        [styles.foregroundMobile]: foregroundImage !== undefined,
    });

    return (
        <>
            <style>{`:root { --hero-min-height: ${height}}`}</style>
            <StyledContainer
                className={containerClasses}
                backgroundColor={backgroundColor}
                backgroundImageDesktop={`url(${backgroundImage})`}
                backgroundImageMobile={`url(${backgroundImageMobile})`}
                includeNavPadding
                eagerHero
            >
                <div className={styles.contentLayer}>
                    <Grid container>
                        <Grid col={{ sm: 12 }}>
                            {breadcrumbs ? breadcrumbs : <div className={styles.noBreadcrumbPlaceholder} />}
                        </Grid>
                    </Grid>

                    <Grid container className={gridClasses}>
                        <Grid col={{ md: 12, lg: 6 }} className={headerColClasses}>
                            {pznMessage && <div className={styles.pznMessageWrapper}>{pznMessage}</div>}
                            <Header
                                className={styles.header}
                                heading={headline}
                                subhead={subheadline}
                                channelNumber={channelNumber}
                                links={links}
                                ctaGroupAlignment={'left'}
                                theme="left"
                                type="heroHeader"
                            />

                            {slideButtons && <div className={styles.buttonGroup}>{slideButtons}</div>}
                        </Grid>
                        <Grid col={{ md: 12, lg: 6 }} className={styles.rightColumn}>
                            {foregroundImage && <div className={foregroundDesktopImageClasses}>{foregroundImage}</div>}
                            {foregroundImageMobile && (
                                <div className={foregroundMobileImageClasses}>{foregroundImageMobile}</div>
                            )}
                        </Grid>
                    </Grid>
                </div>
            </StyledContainer>
            {copyright && (
                <Grid container>
                    <Grid col={{ sm: 12 }}>
                        <div className={styles.copyright}>
                            {copyright || <span dangerouslySetInnerHTML={{ __html: '&nbsp;' }} />}
                        </div>
                    </Grid>
                </Grid>
            )}
        </>
    );
}
